import React, { useMemo } from 'react'
import { useOrderAccountsContext } from 'app/workorder/contexts/use-order-account-context'
import { IOrderAccountData } from '@cbgms/api'
import { EmptyUUID } from '@cbgms/base/utils/uuid'

/**
 * OrderAccountFeatureKeys is a set of features the order account could support
 */
export enum OrderAccountFeatureKey {
  IframeCatalog = 'IFrame',
  IntegratedCatalog = 'Catalog',
  PriceStock = 'PriceStock',
  Search = 'Search',
  Rma = 'RMA',
  Tyres = 'Tyres',
  LicensePlate = 'LicencePlate'
}

interface NeedsOrderAccountFeatureKey {
  orderAccountFeatureKeys: OrderAccountFeatureKey[]
}
interface NeedsOrderSystemNames {
  orderSystemNames: string[]
  excludedOrderSystemNames: string[]
}

// eslint-disable-next-line carsys-custom/typescript-prefer-interface-extend-over-intersection
type NeedsCombined = Partial<NeedsOrderAccountFeatureKey> &
  Partial<NeedsOrderSystemNames> &
  (NeedsOrderAccountFeatureKey | NeedsOrderSystemNames)
type NeedsRequirement = OrderAccountFeatureKey | NeedsCombined

interface OrderAccountProps {
  needs: NeedsRequirement
  orderAccountUUID: string | null // explicitly pass null when the account is not yet loaded
}

const checkOrderAccountFeatureKeys = (keys: OrderAccountFeatureKey[], orderAccountData: IOrderAccountData): boolean => {
  if (keys.length === 0) {
    return true
  }
  return keys.every(key => {
    if (key === OrderAccountFeatureKey.IntegratedCatalog) {
      return orderAccountData.Catalog && orderAccountData.Catalog.UUID !== EmptyUUID
    }

    const feature = orderAccountData.WholesalerOrderSystemServices.find(feature => String(feature.Service) === key && feature.Active)
    return !!feature
  })
}

const checkOrderAccountSystemNames = (
  systemNames: string[],
  excludedOrderSystemNames: string[],
  orderAccountData: IOrderAccountData
): boolean => {
  if (systemNames.length === 0 && excludedOrderSystemNames.length === 0) {
    return true
  }
  return (
    (systemNames.length === 0 || !!systemNames.find(neededName => neededName === orderAccountData.Wholesaler.OrderSystem)) &&
    (excludedOrderSystemNames.length === 0 ||
      !excludedOrderSystemNames.find(excludedName => excludedName === orderAccountData.Wholesaler.OrderSystem))
  )
}

const hasFeature = (needs: NeedsRequirement, orderAccountData: IOrderAccountData | null) => {
  if (!orderAccountData) {
    return false
  }

  const orderSystemNamesArray = (needs as NeedsOrderSystemNames)?.orderSystemNames || []
  const excludedOrderSystemNamesArray = (needs as NeedsOrderSystemNames)?.excludedOrderSystemNames || []
  const hasOrderAccountSystemName = checkOrderAccountSystemNames(orderSystemNamesArray, excludedOrderSystemNamesArray, orderAccountData)

  const keysArray = typeof needs === 'string' ? [needs] : needs?.orderAccountFeatureKeys || []
  const hasOrderAccountFeatureKeys = checkOrderAccountFeatureKeys(keysArray, orderAccountData)
  return hasOrderAccountFeatureKeys && hasOrderAccountSystemName
}

export const useHasOrderAccountFeature = (
  needs: NeedsRequirement,
  orderAccountUUID: string | null // explicitly pass null when the account is not yet loaded
) => {
  const { getOrderAccountInfo } = useOrderAccountsContext()
  const orderAccountData = useMemo(() => getOrderAccountInfo(orderAccountUUID), [getOrderAccountInfo, orderAccountUUID])
  const canShowFeature = useMemo(() => hasFeature(needs, orderAccountData), [needs, orderAccountData])
  return canShowFeature
}

const OrderAccountFeature: React.FC<OrderAccountProps> = ({ needs, orderAccountUUID, children }) => {
  const canShowFeature = useHasOrderAccountFeature(needs, orderAccountUUID)

  if (!orderAccountUUID) {
    return null
  }
  if (!canShowFeature) {
    return null
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}

export default OrderAccountFeature
